import { Slice } from '@tiptap/pm/model';
import type { Node as ProseMirrorNode, ResolvedPos } from '@tiptap/pm/model';
import { Selection, TextSelection } from '@tiptap/pm/state';
import type { Transaction } from '@tiptap/pm/state';
import { TableMap, removeRow } from '@tiptap/pm/tables';

/** The rows of one table a selection reaches into from outside it, inclusive. */
export interface CrossedRows {
	/** Position before the table node. */
	tablePos: number;
	firstRow: number;
	lastRow: number;
}

/**
 * What a selection across a table's edge covers once it is snapped to whole rows, as Word does.
 *
 * `from`/`to` bound everything that goes. A table whose every row is covered sits inside them
 * and needs nothing more; `start`/`end` name a table that only loses SOME rows, at the end the
 * selection starts or finishes in.
 */
export interface TableEdgeSpan {
	from: number;
	to: number;
	start?: CrossedRows;
	end?: CrossedRows;
	/** Only the END lies outside every table, so that is where the caret goes. */
	caretAtEnd: boolean;
}

const isTable = (node: ProseMirrorNode): boolean => node.type.spec['tableRole'] === 'table';

/** The depth of the outermost table holding `$pos` below `depth`, if any. */
const outermostTableBelow = ($pos: ResolvedPos, depth: number): number | null => {
	for (let level = depth + 1; level <= $pos.depth; level += 1) {
		if (isTable($pos.node(level))) {
			return level;
		}
	}
	return null;
};

/**
 * The whole-row span of a text selection with one end inside a table and the other outside it.
 *
 * Null for every other selection. Two cells of one table is a cell selection's business, and
 * ProseMirror's own replace is right for a selection no table edge cuts through.
 */
export const tableEdgeSpan = ($from: ResolvedPos, $to: ResolvedPos): TableEdgeSpan | null => {
	if ($from.pos === $to.pos) {
		return null;
	}
	const shared = $from.sharedDepth($to.pos);
	const sharedRole: unknown = $from.node(shared).type.spec['tableRole'];
	if (sharedRole === 'table' || sharedRole === 'row') {
		return null;
	}
	const startDepth = outermostTableBelow($from, shared);
	const endDepth = outermostTableBelow($to, shared);
	if (startDepth === null && endDepth === null) {
		return null;
	}

	const span: TableEdgeSpan = {
		from: $from.pos,
		to: $to.pos,
		caretAtEnd: startDepth !== null && endDepth === null,
	};

	if (startDepth !== null) {
		const table = $from.node(startDepth);
		const firstRow = $from.index(startDepth);
		if (firstRow === 0) {
			span.from = $from.before(startDepth);
		} else {
			span.from = $from.posAtIndex(firstRow, startDepth);
			span.start = {
				tablePos: $from.before(startDepth),
				firstRow,
				lastRow: table.childCount - 1,
			};
		}
	}

	if (endDepth !== null) {
		const table = $to.node(endDepth);
		// A cell merged down over the next rows takes them with it, or the merge would be split.
		const rowspan: unknown = $to.node(endDepth + 2).attrs['rowspan'];
		const lastRow = Math.min(
			table.childCount - 1,
			$to.index(endDepth) + (typeof rowspan === 'number' ? rowspan - 1 : 0)
		);
		if (lastRow === table.childCount - 1) {
			span.to = $to.after(endDepth);
		} else {
			span.to = $to.posAtIndex(lastRow + 1, endDepth);
			span.end = { tablePos: $to.before(endDepth), firstRow: 0, lastRow };
		}
	}

	return span;
};

/** Removes rows through prosemirror-tables, which carries a merged cell into the rows it keeps. */
const removeRows = (tr: Transaction, { tablePos, firstRow, lastRow }: CrossedRows): void => {
	for (let row = lastRow; row >= firstRow; row -= 1) {
		const table = tr.doc.nodeAt(tablePos);
		if (table === null) {
			return;
		}
		const map = TableMap.get(table);
		removeRow(
			tr,
			{
				map,
				table,
				tableStart: tablePos + 1,
				left: 0,
				right: map.width,
				top: row,
				bottom: row + 1,
			},
			row
		);
	}
};

/**
 * Deletes a span, end table first, so no step moves a position a later one still needs.
 *
 * Leaves the caret at an end that was outside a table, which is where typed text belongs.
 */
const deleteSpan = (tr: Transaction, span: TableEdgeSpan): void => {
	const mapFrom = tr.steps.length;
	const middleFrom =
		span.start === undefined
			? span.from
			: span.start.tablePos + (tr.doc.nodeAt(span.start.tablePos)?.nodeSize ?? 0);
	const middleTo = span.end === undefined ? span.to : span.end.tablePos;

	if (span.end !== undefined) {
		removeRows(tr, span.end);
	}
	if (middleFrom < middleTo) {
		tr.deleteRange(middleFrom, middleTo);
	}
	if (span.start !== undefined) {
		removeRows(tr, span.start);
	}

	const mapping = tr.mapping.slice(mapFrom);
	const caret = span.caretAtEnd ? mapping.map(span.to) : mapping.map(span.from, -1);
	tr.setSelection(Selection.near(tr.doc.resolve(caret), span.caretAtEnd ? 1 : -1));
};

/**
 * A text selection reaching into a table from outside, snapped to whole rows.
 *
 * ProseMirror's generic replace cuts such a selection at the character, and a table cannot be
 * cut there: deleting from a paragraph into row 2 moved the rest of that cell's text out into
 * the paragraph and left a ragged remnant that prosemirror-tables then padded with empty cells.
 * Word snaps the selection to the rows it touches and deletes them, which is what this does.
 * Overriding `replace` covers every route ProseMirror deletes a selection by — Backspace,
 * Delete, typing over it, cut, paste and drag-to-move — the way `CellSelection` does.
 */
export class TableEdgeSelection extends TextSelection {
	get span(): TableEdgeSpan | null {
		return tableEdgeSpan(this.$from, this.$to);
	}

	override replace(tr: Transaction, content: Slice = Slice.empty): void {
		const { span } = this;
		if (span === null) {
			super.replace(tr, content);
			return;
		}
		deleteSpan(tr, span);
		if (content.size > 0) {
			tr.replaceSelection(content);
		}
	}

	override replaceWith(tr: Transaction, node: ProseMirrorNode): void {
		const { span } = this;
		if (span === null) {
			super.replaceWith(tr, node);
			return;
		}
		deleteSpan(tr, span);
		tr.selection.replaceWith(tr, node);
	}

	/** The whole rows, so a cut pastes back what it removed rather than a ragged fragment. */
	override content(): Slice {
		const { span } = this;
		return span === null ? super.content() : this.$from.doc.slice(span.from, span.to, true);
	}
}
